import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AppHeader from './AppHeader';
import { sessionAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const Sessions = () => {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [sites, setSites] = useState([]);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    session_type: 'SY',
    start_date: '',
    end_date: '',
    site: '',
    copy_students_from: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [sessRes, sitesRes] = await Promise.all([
          sessionAPI.getSessions(),
          sessionAPI.getSites ? sessionAPI.getSites() : Promise.resolve({ data: [] }),
        ]);
        if (cancelled) return;
        const list = sessRes.data.results || sessRes.data || [];
        setSessions(Array.isArray(list) ? list : []);
        const siteList = sitesRes.data?.results || sitesRes.data || [];
        setSites(Array.isArray(siteList) ? siteList : []);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleCreate = async () => {
    if (!form.name || !form.start_date || !form.end_date || !form.site) {
      setError('Name, start date, end date, and site are required.');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        session_type: form.session_type,
        start_date: form.start_date,
        end_date: form.end_date,
        site: typeof form.site === 'number' ? form.site : (form.site || null),
      };
      if (form.copy_students_from) payload.copy_students_from = Number(form.copy_students_from);
      const res = await sessionAPI.createSession(payload);
      const data = res.data?.session || res.data;
      const copied = res.data?.students_copied ?? 0;
      setSessions((prev) => [data, ...prev]);
      setForm({ name: '', session_type: 'SY', start_date: '', end_date: '', site: '', copy_students_from: '' });
      if (copied > 0) {
        setError('');
        alert(`Session created. ${copied} students copied from source session. You can remove students after creation.`);
      }
    } catch (e) {
      setError(e.response?.data?.detail || JSON.stringify(e.response?.data) || e.message);
    } finally {
      setCreating(false);
    }
  };

  const copySourceSession = form.copy_students_from
    ? sessions.find((s) => s.id === Number(form.copy_students_from))
    : null;
  const copyCount = copySourceSession?.student_count ?? 0;

  const canCopyStudents = user?.role === 'admin' || user?.role === 'superadmin';
  const [copyDialogSession, setCopyDialogSession] = useState(null);
  const [copySourceId, setCopySourceId] = useState('');
  const [copyPreview, setCopyPreview] = useState(null);
  const [copyPreviewLoading, setCopyPreviewLoading] = useState(false);
  const [copyPreviewError, setCopyPreviewError] = useState('');
  const [copyConfirming, setCopyConfirming] = useState(false);
  const [copyExcludedExpanded, setCopyExcludedExpanded] = useState(false);

  const sourceOptions = copyDialogSession
    ? sessions.filter(
        (s) => s.id !== copyDialogSession.id && (s.site === copyDialogSession.site || s.site_id === copyDialogSession.site_id)
      )
    : [];

  useEffect(() => {
    if (!copyDialogSession || !copySourceId) {
      setCopyPreview(null);
      setCopyPreviewError('');
      return;
    }
    let cancelled = false;
    setCopyPreviewLoading(true);
    setCopyPreviewError('');
    sessionAPI
      .getCopyStudentsPreview(copyDialogSession.id, copySourceId)
      .then((res) => {
        if (!cancelled) setCopyPreview(res.data);
      })
      .catch((err) => {
        if (!cancelled) setCopyPreviewError(err.response?.data?.detail || err.message || 'Failed to load preview');
        setCopyPreview(null);
      })
      .finally(() => {
        if (!cancelled) setCopyPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [copyDialogSession, copySourceId]);

  const openCopyDialog = (session) => {
    setCopyDialogSession(session);
    const others = sessions.filter(
      (s) => s.id !== session.id && (s.site === session.site || s.site_id === session.site_id)
    );
    const sorted = [...others].sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));
    setCopySourceId(sorted.length ? String(sorted[0].id) : '');
    setCopyPreview(null);
    setCopyPreviewError('');
    setCopyExcludedExpanded(false);
  };

  const closeCopyDialog = () => {
    setCopyDialogSession(null);
    setCopySourceId('');
    setCopyPreview(null);
    setCopyPreviewError('');
    setCopyConfirming(false);
  };

  const handleConfirmCopy = () => {
    if (!copyDialogSession || !copySourceId) return;
    setCopyConfirming(true);
    sessionAPI
      .copyStudentsFromSession(copyDialogSession.id, Number(copySourceId))
      .then((res) => {
        const { copied = 0, skipped_already_enrolled = 0 } = res.data || {};
        alert(`Done. ${copied} students copied.${skipped_already_enrolled ? ` ${skipped_already_enrolled} already enrolled and skipped.` : ''}`);
        closeCopyDialog();
        return sessionAPI.getSessions();
      })
      .then((sessRes) => {
        const list = sessRes.data?.results || sessRes.data || [];
        setSessions(Array.isArray(list) ? list : []);
      })
      .catch((err) => {
        alert(err.response?.data?.detail || err.message || 'Copy failed');
      })
      .finally(() => setCopyConfirming(false));
  };

  return (
    <Box>
      <AppHeader backTo="/" />
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Manage Sessions
        </Typography>
        {error && (
          <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Create new session
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label="Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Summer 2026"
              sx={{ minWidth: 160 }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.session_type}
                label="Type"
                onChange={(e) => setForm((p) => ({ ...p, session_type: e.target.value }))}
              >
                <MenuItem value="SY">School Year</MenuItem>
                <MenuItem value="SUMMER">Summer</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Start date"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
            <TextField
              size="small"
              label="End date"
              type="date"
              value={form.end_date}
              onChange={(e) => setForm((p) => ({ ...p, end_date: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 140 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Site</InputLabel>
              <Select
                value={form.site}
                label="Site"
                onChange={(e) => setForm((p) => ({ ...p, site: e.target.value }))}
              >
                <MenuItem value="">Select site</MenuItem>
                {sites.map((site) => (
                  <MenuItem key={site.id} value={site.id}>
                    {site.name || site.slug}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Copy students from</InputLabel>
              <Select
                value={form.copy_students_from}
                label="Copy students from"
                onChange={(e) => setForm((p) => ({ ...p, copy_students_from: e.target.value }))}
              >
                <MenuItem value="">None</MenuItem>
                {sessions.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.display_name || s.name} ({s.student_count ?? 0} students)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCreate} disabled={creating}>
              {creating ? 'Creating...' : 'Create session'}
            </Button>
          </Box>
          {form.copy_students_from && copyCount !== undefined && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {copyCount} students will be copied from {copySourceSession?.display_name || copySourceSession?.name}. You can remove students after creation.
            </Typography>
          )}
        </Paper>

        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Start</TableCell>
                  <TableCell>End</TableCell>
                  <TableCell>Students</TableCell>
                  {canCopyStudents && <TableCell>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell>{s.display_name || s.name}</TableCell>
                    <TableCell>{s.session_type || '—'}</TableCell>
                    <TableCell>{s.start_date}</TableCell>
                    <TableCell>{s.end_date}</TableCell>
                    <TableCell>{s.student_count ?? 0}</TableCell>
                    {canCopyStudents && (
                      <TableCell>
                        <Button size="small" variant="outlined" onClick={() => openCopyDialog(s)}>
                          Copy students from previous session
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={!!copyDialogSession} onClose={closeCopyDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Copy students from previous session</DialogTitle>
          <DialogContent>
            {copyDialogSession && (
              <Box sx={{ pt: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Destination: {copyDialogSession.display_name || copyDialogSession.name}
                </Typography>
                <FormControl size="small" fullWidth sx={{ mt: 2, mb: 2 }}>
                  <InputLabel>Copy from session</InputLabel>
                  <Select
                    value={copySourceId}
                    label="Copy from session"
                    onChange={(e) => setCopySourceId(e.target.value)}
                  >
                    <MenuItem value="">Select session</MenuItem>
                    {sourceOptions.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {s.display_name || s.name} ({s.student_count ?? 0} students)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {copyPreviewError && <Alert severity="error" sx={{ mb: 2 }}>{copyPreviewError}</Alert>}
                {copyPreviewLoading && <Typography color="text.secondary">Loading preview…</Typography>}
                {copyPreview && !copyPreviewLoading && (
                  <Box>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {copyPreview.to_copy_count} students will be copied
                    </Typography>
                    {copyPreview.excluded_count > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                          {copyPreview.excluded_count} students excluded — aged out
                        </Typography>
                        <Button
                          size="small"
                          startIcon={copyExcludedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          onClick={() => setCopyExcludedExpanded((e) => !e)}
                          sx={{ mt: 0.5 }}
                        >
                          {copyExcludedExpanded ? 'Hide list' : 'Show list'}
                        </Button>
                        <Collapse in={copyExcludedExpanded}>
                          <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 1 }}>
                            {(copyPreview.excluded || []).map((st) => (
                              <li key={st.id}>
                                <Typography variant="body2">
                                  {st.last_name}, {st.first_name} — DOB {st.date_of_birth || '—'} — {st.reason}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        </Collapse>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeCopyDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleConfirmCopy}
              disabled={!copySourceId || copyConfirming || copyPreviewLoading}
            >
              {copyConfirming ? 'Copying…' : 'Confirm copy'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Sessions;
